"use client";

import * as React from "react";
import { shortenAddress } from "../../../../lib/format";
import { CheckIcon } from "../../../wallet-info/components/icons";
import type { SendFlow } from "../../model/useSendFlow";
import { ErrorCircleIcon, SuccessCircleIcon } from "../icons";

export function SendResult({ flow }: { flow: SendFlow }) {
  const { status, txHash, error, copyHash, hashCopied, openTransaction } = flow;
  const isError = status === "error";
  const isConfirmed = status === "success";

  if (isError) {
    return (
      <div className="sf-body sf-result sf-result--error">
        <span className="sf-result-icon">
          <ErrorCircleIcon size={56} />
        </span>
        <div className="sf-result-copy">
          <h3 className="sf-result-title">Transaction Failed</h3>
          <p className="sf-result-desc">
            {error || "Something went wrong. Please try again."}
          </p>
        </div>
        {txHash && (
          <HashCard
            txHash={txHash}
            hashCopied={hashCopied}
            onCopy={copyHash}
            onOpen={openTransaction}
          />
        )}
      </div>
    );
  }

  return (
    <div className="sf-body sf-result">
      <span className="sf-result-icon" data-pending={!isConfirmed || undefined}>
        {isConfirmed ? <SuccessCircleIcon size={56} /> : <span className="sf-result-spinner" />}
      </span>
      <div className="sf-result-copy">
        <h3 className="sf-result-title">
          {isConfirmed ? "Transaction Complete" : "Transaction in Progress"}
        </h3>
        <p className="sf-result-desc">
          {isConfirmed
            ? "Your transfer was sent successfully."
            : "Transfer in progress. Waiting for confirmation."}
        </p>
      </div>
      {txHash && (
        <HashCard
          txHash={txHash}
          hashCopied={hashCopied}
          onCopy={copyHash}
          onOpen={openTransaction}
        />
      )}
    </div>
  );
}

function HashCard({
  txHash,
  hashCopied,
  onCopy,
  onOpen,
}: {
  txHash: string;
  hashCopied: boolean;
  onCopy: () => void;
  onOpen: () => void;
}) {
  return (
    <div className="sf-result-hash-card">
      <span className="sf-result-hash-label">Transaction Hash</span>
      <div className="sf-result-hash-row">
        <button
          type="button"
          className="sf-result-hash-link"
          onClick={onOpen}
          aria-label="Open transaction in explorer"
          data-track="tx_link_open"
        >
          {shortenAddress(txHash)}
        </button>
        <button
          type="button"
          className="sf-result-copy-btn"
          onClick={onCopy}
          aria-label="Copy transaction hash"
          data-track="tx_hash_copy"
        >
          {hashCopied ? <CheckIcon size={16} /> : "Copy"}
        </button>
      </div>
    </div>
  );
}
